import React, { useState, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from '@tanstack/react-router';
import PropTypes from 'prop-types';
import { useAuth } from '@bb/session-manager';

import withStyles from '@mui/styles/withStyles';
import { AppBar, Toolbar } from '@mui/material';

import {
  BLACKBOILER_ADMIN_PERMISSION,
  CONTRACTS_PERMISSION,
  CLAUSE_EDITOR_PERMISSION,
  COMPANY_ADMIN_PERMISSION,
  PLAYBOOK_BUILDER_PERMISSION,
  FAST_TRACK_PERMISSION,
  METRICS_DASHBOARD_PERMISSION,
  METRICS_FASTTRACK_PERMISSION,
  POA_INTAKE_PERMISSION,
  POA_REVIEW_PERMISSION,
  POA_GEMS_PERMISSION,
} from '../../groups.config';
import { SetCurrentModelData } from '../../redux/actions/modelActions';
import { setErrMsg } from '../../redux/actions/snackbarActions';
import { GetModelBasics } from '../../requests/trainingModel';
import {
  HOME_ROUTE,
  CLAUSES_ROUTE,
  CONTRACTS_ROUTE,
  USERS_ROUTE,
  SETTINGS_ROUTE,
  // MODEL_ROUTE,
  DOCSERVER_ROUTE,
  DOCSERVER_READONLY_ROUTE,
  MODEL_ROUTE,
  MODEL_EDIT_ROUTE,
  FAST_TRACK_ROUTE,
  ANALYTICS_ROUTE,
  REPORTING_ROUTE,
  TENANT_DASHBOARD_ROUTE,
  LLM_STATS_ROUTE,
  TEXT_INDEXER_URL,
  POA_ROUTE,
  INTEGRATIONS_ROUTE,
} from '../../routes.config';
import { ExportButton } from '../ExportButton';
import AppBarBreadCrumb from './AppBarBreadCrumb';
import AppBarFull from './AppBarFull';
import Styles from './styles';

function BaseAppBar({ classes }) {
  const [company, setCompany] = useState();
  const { pathname } = useLocation();
  const { config, user, auth } = useAuth();

  // strict: false allows accessing params from any matched route (needed for layout components)
  const { id = null } = useParams({ strict: false });
  const dispatch = useDispatch();

  const modelData = useSelector((state) => state.Model.currentModelData, shallowEqual);
  const availableSections = useSelector(
    (state) => state.PlaybookBuilder.availableSections,
    shallowEqual
  );

  const setErr = (value) => dispatch(setErrMsg(value));

  useEffect(() => {
    if (config) {
      if (config.displayName) {
        setCompany(config.displayName);
      } else {
        setCompany(capitalize(config.name));
      }
    }
  }, [config]);

  useEffect(() => {
    if (id && !modelData) {
      // refresh model data basics asynchronously if unavailable and on a model path

      GetModelBasics(id)
        .then((model) => {
          dispatch(SetCurrentModelData(model));
        })
        .catch((err) => {
          setErr('Failed to retrieve model data');
          throw err;
        });
    }
  }, [id]);

  const capitalize = (str) =>
    str
      .toLowerCase()
      .split(' ')
      .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');

  const navList = [{ label: 'Home', path: HOME_ROUTE, role: true }];

  if (import.meta.env.DEV) {
    console.debug('[BaseAppBar] Building navList', {
      hasUser: !!user,
      hasConfig: !!config,
      isBlackboilerAdmin: user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION),
    });
  }
  if (config && user) {
    // Dynamically build the navigation list based on user permissions and config
    navList.push({
      label: 'Contracts',
      path: CONTRACTS_ROUTE,
      role: user?.permissions?.has(CONTRACTS_PERMISSION),
    });
    if (config.showClauseLibrary) {
      navList.push({
        label: 'Clause Library',
        path: CLAUSES_ROUTE,
        role: user?.permissions?.has(CLAUSE_EDITOR_PERMISSION),
      });
    }
    if (config.showPlaybookBuilder) {
      navList.push({
        label: 'Playbook Builder',
        path: MODEL_ROUTE,
        role: user?.permissions?.has(PLAYBOOK_BUILDER_PERMISSION),
      });
    }
    if (config.showFastTrack) {
      navList.push({
        label: 'Fast Track',
        path: FAST_TRACK_ROUTE,
        role: user?.permissions?.has(FAST_TRACK_PERMISSION),
      });
    }
    if (config.showPOA) {
      navList.push({
        label: 'PoA',
        path: POA_ROUTE,
        role:
          user?.permissions?.has(POA_INTAKE_PERMISSION) ||
          user?.permissions?.has(POA_GEMS_PERMISSION) ||
          user?.permissions?.has(POA_REVIEW_PERMISSION),
      });
    }
    if (config.showAnalytics) {
      navList.push({
        label: 'Analytics',
        path: ANALYTICS_ROUTE,
        role:
          user?.permissions?.has(METRICS_DASHBOARD_PERMISSION) ||
          user?.permissions?.has(METRICS_FASTTRACK_PERMISSION),
      });
    }
    if (config.showReportingDashboard) {
      navList.push({
        label: 'Reporting',
        path: REPORTING_ROUTE,
        role: user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION),
      });
    }
    if (config.showIntegrations) {
      navList.push({
        label: 'Integrations',
        path: INTEGRATIONS_ROUTE,
        role: user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION),
      });
    }
    navList.push({
      label: 'Users',
      path: USERS_ROUTE,
      role: user?.permissions?.has(COMPANY_ADMIN_PERMISSION),
    });
    navList.push({
      label: 'Settings',
      path: SETTINGS_ROUTE,
      role: user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION),
    });
  }
  console.log('[BaseAppBar] Admin check: BLACKBOILER_ADMIN_PERMISSION =', BLACKBOILER_ADMIN_PERMISSION,
    'user has it:', user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION),
    'config.useTenantManagement:', config?.useTenantManagement,
    'TEXT_INDEXER_URL:', TEXT_INDEXER_URL);

  if (user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION)) {
    const adminChildren = [
      ...(config?.useTenantManagement
        ? [{ label: 'Tenant Dashboard', path: TENANT_DASHBOARD_ROUTE }]
        : []),
      { label: 'LLM Stats', path: LLM_STATS_ROUTE },
      { label: 'Text Indexer', path: TEXT_INDEXER_URL, external: true },
    ];
    console.log('[BaseAppBar] Pushing Admin nav item with children:', adminChildren);
    navList.push({
      label: 'Admin',
      role: true,
      children: adminChildren,
    });
  } else {
    console.log('[BaseAppBar] User does NOT have BLACKBOILER_ADMIN_PERMISSION — Admin nav item not added');
  }

  console.log('[BaseAppBar] Final navList:', JSON.stringify(navList.map(n => ({ label: n.label, role: n.role, hasChildren: !!n.children }))));

  const playbookBuilderAnswerQuestionsLookupMap = {
    select: 'Answer Questions',
    status: 'Answer Questions',
  };
  if (availableSections) {
    availableSections.forEach(({ className, name }) => {
      playbookBuilderAnswerQuestionsLookupMap[className] = name;
    });
  }

  // Extract userName from user object - display full name
  let userName = '';
  if (user) {
    // First try to get name from auth.user.profile (OIDC/Auth0 profile)
    if (auth?.user?.profile?.given_name && auth?.user?.profile?.family_name) {
      userName = `${auth.user.profile.given_name} ${auth.user.profile.family_name}`;
    } else if (auth?.user?.profile?.name) {
      userName = auth.user.profile.name;
    } else if (auth?.user?.profile?.given_name) {
      userName = auth.user.profile.given_name;
    }
    // Then try API user object fields (toShortProfile returns fullName)
    else if (user.fullName) {
      userName = user.fullName;
    } else if (user.name?.first) {
      userName = user.name.full || `${user.name.first} ${user.name.last || ''}`.trim();
    } else if (typeof user.name === 'string' && user.name) {
      userName = user.name;
    } else if (user.email) {
      // Fallback to email if no name fields available
      userName = user.email;
    }
  }

  const renderSwitch = () => {
    const playbookBuilderAnswerQuestionsRegex = new RegExp(
      `${MODEL_EDIT_ROUTE}/[0-9a-z]*/rules/aq/.*`,
      'g'
    );

    const breadcrumbs = [];

    switch (true) {
      case DOCSERVER_ROUTE === pathname:
        breadcrumbs.push(navList[1]);
        breadcrumbs.push({ label: 'View Contract', path: pathname });
        return (
          <AppBar position="fixed" className={classes.root}>
            <Toolbar>
              <AppBarBreadCrumb
                pathname={pathname}
                breadCrumbs={breadcrumbs}
                buttonRight={<ExportButton />}
              />
            </Toolbar>
          </AppBar>
        );
      case DOCSERVER_READONLY_ROUTE === pathname:
        breadcrumbs.push(navList[1]);
        breadcrumbs.push({ label: 'View Archived', path: pathname });
        return (
          <AppBar position="fixed" className={classes.root}>
            <Toolbar>
              <AppBarBreadCrumb pathname={pathname} breadCrumbs={breadcrumbs} />
            </Toolbar>
          </AppBar>
        );
      case (pathname.match(playbookBuilderAnswerQuestionsRegex) || []).length > 0:
        // SPECIAL ROUTING FOR ANSWER QUESTIONS PLAYBOOK BUILDER FLOW
        const splitPath = pathname.split('/rules/aq');

        if (splitPath.length !== 2) {
          // default if no path specified
          return (
            <AppBar position="fixed" className={classes.root}>
              <Toolbar>
                <AppBarFull
                  pathname={pathname}
                  navList={navList}
                  companyName={company}
                  userName={userName}
                />
              </Toolbar>
            </AppBar>
          );
        }
        // e.g. /bbweb/model/edit/602fe1a3f554de005a02c371/rules/aq/select/example => ['select', 'example']
        const paths = splitPath
          .at(-1)
          .split('/')
          .filter((o) => o.length > 0);

        let gradualPath = `${splitPath[0]}/rules/aq`;
        // add core breadcrumb paths
        if (user?.permissions?.has(PLAYBOOK_BUILDER_PERMISSION)) {
          breadcrumbs.push({
            label: modelData ? modelData.name : '',
            path: `${splitPath[0]}`,
          });
        }
        if (paths.filter((path) => path === 'select' || path === 'status').length === 0) {
          // not on /select or /status pages
          breadcrumbs.push({ label: 'Answer Questions', path: `${gradualPath}/status` });
        }

        paths.forEach((path) => {
          gradualPath += `/${path}`;
          breadcrumbs.push({
            label: playbookBuilderAnswerQuestionsLookupMap[path],
            path: gradualPath,
          });
        });

        return (
          <AppBar position="fixed" className={classes.root}>
            <Toolbar>
              <AppBarBreadCrumb
                pathname={pathname}
                breadCrumbs={breadcrumbs}
                returnLabelText="Model"
                returnLabelLink={splitPath[0]}
              />
            </Toolbar>
          </AppBar>
        );
      default:
        return (
          <AppBar position="fixed" className={classes.root}>
            <Toolbar>
              <AppBarFull
                pathname={pathname}
                navList={navList}
                companyName={company}
                userName={userName}
              />
            </Toolbar>
          </AppBar>
        );
    }
  };

  return !!auth?.user && !!user && renderSwitch();
}

BaseAppBar.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(Styles)(BaseAppBar);
